import React, { useState, useEffect, useRef } from 'react';
import { Highlighter, X, Tag } from 'lucide-react';

/**
 * HighlightPopup Component
 *
 * Appears when user selects text within the College Notebook.
 * Allows them to save the highlighted text with an optional category tag.
 *
 * Features:
 * - Positioned near the selected text
 * - Category selection for organization
 * - Quick save or cancel
 * - Highlights the selected text visually
 */
export default function HighlightPopup({
  selectedText,
  selection,
  onSave,
  onCancel,
  position
}) {
  const [category, setCategory] = useState('positioning');
  const [saving, setSaving] = useState(false);
  const popupRef = useRef(null);

  // Available categories
  const categories = [
    { value: 'positioning', label: 'Positioning', color: 'indigo' },
    { value: 'research', label: 'Research', color: 'azure' },
    { value: 'values', label: 'Values', color: 'coral' },
    { value: 'advantages', label: 'Advantages', color: 'teal' },
    { value: 'strategy', label: 'Strategy', color: 'gold' },
    { value: 'general', label: 'General', color: 'emerald' }
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        text: selectedText,
        category,
        selection
      });
    } catch (error) {
      console.error('Error saving highlight:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedText, category]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  const getCategoryColor = (cat) => {
    const colors = {
      indigo: 'bg-indigo-500 hover:bg-indigo-600',
      azure: 'bg-azure-500 hover:bg-azure-600',
      coral: 'bg-coral-500 hover:bg-coral-600',
      teal: 'bg-teal-500 hover:bg-teal-600',
      gold: 'bg-gold-500 hover:bg-gold-600',
      emerald: 'bg-emerald-500 hover:bg-emerald-600'
    };
    return colors[cat] || colors.teal;
  };

  return (
    <div
      ref={popupRef}
      className="fixed bg-white rounded-lg shadow-2xl border-2 border-slate-900 p-3 z-[60]"
      style={{
        top: position.top,
        left: position.left,
        transform: 'translateY(8px)',
        minWidth: '280px',
        maxWidth: '320px'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Highlighter className="w-4 h-4 text-amber-600" />
          <h3 className="font-semibold text-sm text-slate-800">Save Highlight</h3>
        </div>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-slate-100 rounded transition-colors"
          title="Cancel (Esc)"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Selected Text Preview */}
      <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-slate-700 max-h-20 overflow-y-auto">
        "{selectedText}"
      </div>

      {/* Category Selection */}
      <div className="mb-3">
        <label className="flex items-center gap-1 text-xs font-medium text-slate-700 mb-2">
          <Tag className="w-3 h-3" />
          Category
        </label>
        <div className="flex flex-wrap gap-1">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-2 py-1 text-xs rounded-full transition-all ${
                category === cat.value
                  ? `${getCategoryColor(cat.color)} text-white font-medium`
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-3 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg
                   hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Highlighter className="w-4 h-4" />
              Save Highlight
            </>
          )}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg
                   hover:bg-slate-200 transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Keyboard Shortcut Hint */}
      <div className="mt-2 text-xs text-slate-400 text-center">
        ⌘/Ctrl+Enter to save • Esc to cancel
      </div>
    </div>
  );
}
